/**
 * Studies tab — plan §11 item 2 ("studies and their negotiations, tags,
 * recruitment difficulty, turnaround dates, status, the outcome note"),
 * built by §13.4 WP-34. The outcome note (§5.6) is out of this WP's owned
 * scope (notes/**) — every other piece of this tab lives here.
 *
 * WP-103, decision (290), 2026-09-29: a study and a negotiation each open as a
 * page (StudyPage, NegotiationPage) under this tab's routes, with a
 * breadcrumb; the stacked modals are gone.
 */
import { useEffect, useState, useMemo, useRef } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Plus, FlaskConical } from 'lucide-react';
import { PageHeader, SearchInput, FilterSelect, ToastProvider, EmptyState, primaryBtnCls } from '../../../ui';
import { useContracts } from '../../ContractsProvider';
import { listStudies } from '../../api/client';
import type { Study } from '../../api/client';
import { CreateStudyModal } from './CreateStudyModal';
import { StudyStatusPill } from './pills';
import { StudyPage } from './StudyPage';
import { NegotiationPage } from './NegotiationPage';

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
];

interface StudiesListProps {
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
}

function StudiesList({ search, onSearchChange, statusFilter, onStatusFilterChange }: StudiesListProps) {
  const { siteId } = useContracts();
  const navigate = useNavigate();
  const [studies, setStudies] = useState<Study[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    listStudies()
      .then((rows) => {
        if (!cancelled) setStudies(rows);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not load studies.');
      });
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  const filtered = useMemo(() => {
    if (!studies) return [];
    const term = search.trim().toLowerCase();
    return studies.filter((s) => {
      if (statusFilter && s.status !== statusFilter) return false;
      if (!term) return true;
      const haystack = [s.protocol_number, s.title, s.tags.sponsorName, s.tags.croName, s.tags.therapeuticAreaName, s.tags.indicationName]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [studies, search, statusFilter]);

  function handleCreated(study: Study) {
    setStudies((prev) => [...(prev ?? []), study]);
  }

  if (!siteId) {
    return <EmptyState icon={FlaskConical} title="No site selected" hint="Choose a site to see its studies." />;
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Studies"
        icon={FlaskConical}
        count={studies?.length ?? undefined}
        subtitle="Studies and their negotiations, tags, recruitment difficulty, turnaround dates and status."
        actions={
          <button type="button" onClick={() => setShowCreate(true)} className={primaryBtnCls}>
            <Plus className="h-4 w-4" />
            New study
          </button>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput value={search} onChange={onSearchChange} placeholder="Search protocol, title, sponsor…" className="flex-1 basis-64" />
        <FilterSelect label="Status" value={statusFilter} onChange={onStatusFilterChange} options={STATUS_FILTER_OPTIONS} auto />
      </div>

      {loadError && <p className="text-sm text-red-600 dark:text-red-400">{loadError}</p>}

      {studies === null && !loadError && <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>}

      {studies !== null && filtered.length === 0 && (
        <EmptyState
          icon={FlaskConical}
          title={studies.length === 0 ? 'No studies yet' : 'No studies match'}
          hint={studies.length === 0 ? 'Create the first study to start tracking its negotiations.' : 'Try a different search or status.'}
          variant="card"
        />
      )}

      {filtered.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                {['Protocol', 'Sponsor', 'CRO', 'Therapeutic area', 'Indication', 'Recruitment', 'Status'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filtered.map((s) => (
                <tr
                  key={s.id}
                  data-testid={`study-row-${s.id}`}
                  onClick={() => navigate(`/contracts/studies/${s.id}`)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') navigate(`/contracts/studies/${s.id}`);
                  }}
                  className="cursor-pointer transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 dark:hover:bg-gray-700/50"
                >
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                    {s.protocol_number}
                    {s.title && <div className="text-xs font-normal text-gray-500 dark:text-gray-400">{s.title}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{s.tags.sponsorName ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{s.tags.croName ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{s.tags.therapeuticAreaName ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{s.tags.indicationName ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{s.recruitment_difficulty ?? '—'}</td>
                  <td className="px-4 py-3">
                    <StudyStatusPill status={s.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateStudyModal isOpen={showCreate} onClose={() => setShowCreate(false)} onCreated={handleCreated} />
    </div>
  );
}

export function StudiesTab() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  // <main> (Layout.tsx) is the scroller: a new page starts at its top.
  useEffect(() => {
    const main = wrapperRef.current?.closest('main');
    if (main) main.scrollTop = 0;
  }, [pathname]);

  return (
    <ToastProvider>
      <div ref={wrapperRef} className="mx-auto w-full max-w-[1700px] p-4 sm:p-6 lg:p-8">
        <Routes>
          <Route
            index
            element={
              <StudiesList search={search} onSearchChange={setSearch} statusFilter={statusFilter} onStatusFilterChange={setStatusFilter} />
            }
          />
          <Route path=":studyId" element={<StudyPage />} />
          <Route path=":studyId/negotiations/:negotiationId" element={<NegotiationPage />} />
          <Route path="*" element={<Navigate to="/contracts/studies" replace />} />
        </Routes>
      </div>
    </ToastProvider>
  );
}

export default StudiesTab;
